/**
 * The letters and points, and what each is called.
 *
 * Here rather than in either page that needs them: the workbench names a point
 * in a picker and the glyph map names the same point in a review, and two
 * spellings of one vocabulary is how a page comes to call קמץ something the
 * other one does not.
 *
 * The NAMES only. Which letters and points a FACE holds comes from the face —
 * a font that draws no final mem should not be offered one — so nothing here is
 * a claim about any font.
 */

/**
 * Every letter a point can sit on, in the order a chart reads.
 *
 * A final follows the letter it is a form of rather than sitting in a group at
 * the end, because that is where somebody checking the alefbeis expects it and
 * because the pair is what gets compared: ך against כ is the reading, and a
 * final gathered up with the other four is a letter nobody looks at twice.
 */
export const LETTERS = 'אבגדהוזחטיכךלמםנןסעפףצץקרשת'

/**
 * The points, in the order they are taught.
 *
 * Not codepoint order, which interleaves the te'amim and puts dagesh among the
 * vowels: this is the sequence somebody reciting them uses, so a review runs
 * the way a person already holds the set.
 */
export const POINTS: { cp: string; name: string }[] = [
  { cp: '05B0', name: 'שוא' },
  { cp: '0311', name: 'שוא נע' },
  { cp: '05B1', name: 'חטף סגול' },
  { cp: '05B2', name: 'חטף פתח' },
  { cp: '05B3', name: 'חטף קמץ' },
  { cp: '05B4', name: 'חיריק' },
  { cp: '05B5', name: 'צירי' },
  { cp: '05B6', name: 'סגול' },
  { cp: '05B7', name: 'פתח' },
  { cp: '05B8', name: 'קמץ' },
  { cp: '05C7', name: 'קמץ קטן' },
  { cp: '05B9', name: 'חולם' },
  { cp: '05BA', name: 'חולם חסר' },
  { cp: '05BB', name: 'קובוץ' },
  { cp: '05BC', name: 'דגש' },
  { cp: '05BF', name: 'רפה' },
  { cp: '05C1', name: 'שין' },
  { cp: '05C2', name: 'שין שמאלית' },
  { cp: '05BD', name: 'מתג' },
]

/**
 * The cantillation, in codepoint order.
 *
 * Not the order they are taught, which is by function — conjunctive against
 * disjunctive, and by rank within each — because that order is a claim about
 * how a verse divides and this list is only about where a mark is drawn. Kept
 * apart from POINTS for the same reason a face may carry one set and not the
 * other: Alephbeis Dyslexia holds no te'amim at all, and a chart that offered
 * them would report thirty gaps that are not faults.
 */
export const TAAMIM: { cp: string; name: string }[] = [
  { cp: '0591', name: 'אתנח' },
  { cp: '0592', name: 'סגולתא' },
  { cp: '0593', name: 'שלשלת' },
  { cp: '0594', name: 'זקף קטן' },
  { cp: '0595', name: 'זקף גדול' },
  { cp: '0596', name: 'טפחא' },
  { cp: '0597', name: 'רביע' },
  // Named for what it IS rather than for what Unicode calls it. The two are
  // swapped there: U+0598 is `HEBREW ACCENT ZARQA` and U+05AE is `ZINOR`, but
  // the mark written as zarqa through the twenty-one books is U+05AE, and this
  // one is the צנורית of Tehillim, Mishlei and Iyov. Carrying the codepoint's
  // name would put זרקא twice in one chart, on the common mark and on the rare
  // one, which is the confusion this table exists to settle.
  { cp: '0598', name: 'צנורית' },
  { cp: '0599', name: 'פשטא' },
  { cp: '059A', name: 'יתיב' },
  { cp: '059B', name: 'תביר' },
  { cp: '059C', name: 'גרש' },
  { cp: '059D', name: 'גרש מוקדם' },
  { cp: '059E', name: 'גרשיים' },
  { cp: '059F', name: 'קרני פרה' },
  { cp: '05A0', name: 'תלישא גדולה' },
  { cp: '05A1', name: 'פזר' },
  { cp: '05A2', name: 'אתנח הפוך' },
  { cp: '05A3', name: 'מונח' },
  { cp: '05A4', name: 'מהפך' },
  { cp: '05A5', name: 'מרכא' },
  { cp: '05A6', name: 'מרכא כפולה' },
  { cp: '05A7', name: 'דרגא' },
  // One mark under two names: it is קדמא standing alone and אזלא where it is
  // the one written before a גרש. Both are here because a chart naming only
  // the first reads, to somebody looking for the second, as a mark the face
  // does not carry — and there is no second codepoint to go and find.
  { cp: '05A8', name: 'קדמא · אזלא' },
  { cp: '05A9', name: 'תלישא קטנה' },
  { cp: '05AA', name: 'ירח בן יומו' },
  { cp: '05AB', name: 'עולה' },
  { cp: '05AC', name: 'עילוי' },
  { cp: '05AD', name: 'דחי' },
  { cp: '05AE', name: 'צנור' },
]

/**
 * Not a vowel, and so not what a meteg is written beside.
 *
 * The dagesh, the rafe and the shin dots say something about the LETTER rather
 * than sounding it. The sheva is the one that has to be argued: it marks the
 * absence of a vowel, and a meteg is not written with it. The chatafs stay —
 * they are sounded, and the face draws a glyph for each of them with a meteg,
 * which is the strongest statement it makes about which pairs occur.
 */
const NOT_A_VOWEL = new Set([
  '05BC',
  '05BF',
  '05C1',
  '05C2',
  '0311',
  '05BD',
  '05B0',
])

/** The points that are vowels, which is what a meteg or a ta'am accompanies. */
export const VOWELS = POINTS.filter((p) => !NOT_A_VOWEL.has(p.cp))

/**
 * The gutturals. They take a chataf where another letter takes a sheva.
 */
const GUTTURALS = 'אהחע'

/**
 * The letters no dagesh enters.
 *
 * Not the gutturals, which is the near-miss: HEH takes one, and it is the
 * mappiq — written at the end of a word to say the heh is sounded rather than
 * silent. It is U+05BC like any other dagesh, so a chart that refuses the
 * gutturals wholesale puts mappiq-heh out of reach, and where the point sits
 * inside a heh is then the one dagesh nobody can check.
 *
 * Resh joins them instead. It classically takes no dagesh, and offering one
 * draws a form the language does not write.
 */
const NO_DAGESH = 'אחער'

/** The letters a rafe is written on: the begadkefat, and heh. */
const RAFE_LETTERS = 'בגדהכפת'

/** The five that close a word. */
const FINALS = 'ךםןףץ'

/**
 * Which finals carry which point. A final not named against a point takes none.
 *
 * A final closes its word, so there is usually no vowel after it to write —
 * which makes the few that do occur worth naming one at a time rather than
 * inferring. Final kaf is the common one: the second-person suffix puts a qamats
 * or a sheva under it constantly, and it takes a DAGESH beside the qamats in
 * forms like אֲשַׁחֲרֶךָּ — the one place a final letter is doubled, against the
 * ordinary rule that a dagesh opens a syllable rather than closing one.
 *
 * Attested for kaf in a vocalized corpus at 738 shevas, 225 qamatzim and one
 * qamats-with-dagesh. Final nun's qamats is not in that text and occurs
 * regardless, which is the whole reason this list is authored rather than
 * counted.
 */
const FINALS_FOR: Record<string, string> = {
  '05B8': 'ךן',
  '05B0': 'ך',
  '05BC': 'ך',
}

/**
 * Which letters a point sits on, where that is genuinely constrained.
 *
 * Narrow on purpose. A constraint is stated here only where the letter itself
 * decides it: a dot that distinguishes shin from sin has no meaning on another
 * letter, a rafe says a letter is soft and so goes only where softness is a
 * question, and a chataf is the sheva a guttural can pronounce.
 * Everything else returns the whole alefbeis.
 *
 * **A pair missing from a text is not a pair that cannot occur**, and building
 * this list the other way round is how it goes wrong. A corpus of thirty
 * thousand vocalized words shows no vowel on a final nun — and a final nun takes
 * a qamats, which that text simply had no word for. Absence of a form is
 * evidence about the passage, never about the language.
 *
 * The other reason to stay narrow: a face is judged on what it PLACES, which is
 * a wider question than what is written. A teaching chart sets every letter
 * against every point deliberately, and a review that hid the rare ones would
 * hide exactly the placements nobody has checked.
 */
export function lettersFor(cp: string): string {
  const point = cp.toUpperCase()
  // The shin and sin dots say which letter this is, and no other letter is in
  // question.
  if (point === '05C1' || point === '05C2') return 'ש'
  // The rafe says a letter is soft, so it is written where softness is a
  // question: the begadkefat without their dagesh, and a heh without its
  // mappiq.
  if (point === '05BF') return RAFE_LETTERS
  // The mark that says a sheva is vocal goes where a VOCAL sheva goes, which
  // is not everywhere a sheva goes. A guttural takes a chataf where another
  // letter takes a vocal sheva, and a sheva closing a word is silent — so on a
  // guttural or a final the mark would draw a form that does not occur, and the
  // chart would demonstrate the mark on one.
  // Holam haser for vav is a cholam on a vav that is a consonant, kept from
  // being read as holam male; on any other letter it is simply a cholam.
  if (point === '05BA') return 'ו'
  if (point === '0311') {
    return [...lettersFor('05B0')]
      .filter((c) => !GUTTURALS.includes(c) && !FINALS.includes(c))
      .join('')
  }
  // A meteg and a ta'am are written on whatever letter carries the vowel they
  // accompany, so neither narrows the set the way a shin dot or a dagesh does.
  if (point === '05BD' || TAAMIM.some((t) => t.cp === point)) {
    return [...LETTERS].filter((c) => !FINALS.includes(c)).join('')
  }
  // A dagesh enters neither a guttural nor a resh — except a heh, where it is
  // the mappiq. That is why the letters it sits on are their own list rather
  // than the gutturals.
  const refused = point === '05BC' ? NO_DAGESH : ''
  // A chataf is a sheva a guttural can pronounce.
  if (point === '05B1' || point === '05B2' || point === '05B3') {
    return GUTTURALS
  }
  // Every ordinary letter, and only the finals that carry this one.
  const finals = FINALS_FOR[point] ?? ''
  return [...LETTERS]
    .filter((c) => !refused.includes(c))
    .filter((c) => !FINALS.includes(c) || finals.includes(c))
    .join('')
}

/**
 * Points written on a letter together with this one.
 *
 * Most points have the letter to themselves. These SHARE A CORNER — a cholam
 * and the shin and sin dots all sit above, at the same one, so when two of them
 * meet one has to move over.
 *
 * That makes the pair a case in its own right — a face can place either
 * correctly on its own and collide when they meet, and reviewing one at a time
 * would never show it. So a point that has company is reviewed with and
 * without. A point that is never written WITHOUT another is a different
 * relation: see `writtenWith`.
 */
export function companionsFor(cp: string): string[] {
  const point = cp.toUpperCase()
  // Gathered rather than answered by the first rule that matches: a cholam
  // shares the corner above with a shin dot AND takes a meteg below, so a
  // return on the first of those would drop the second and the pair the face
  // draws would never be shown on the one vowel that has both.
  const beside: string[] = []
  if (point === '05C1' || point === '05C2') beside.push('05B9')
  if (point === '05B9') beside.push('05C1', '05C2')
  // A meteg is written UNDER the letter beside its vowel, so the two share the
  // space below rather than a corner — and a vowel occurs with and without one,
  // which is what makes it a companion rather than a thing never written alone.
  // It is the pair this face draws specially: a precomposed glyph for each
  // chataf with a meteg, and no other combination of two points.
  // The meteg names the vowels and they do not name it back, so the pair is
  // reviewed by CHOOSING the meteg. Named both ways it doubles every vowel's
  // row with a mark the row is not about — twenty-two letters shown twice to
  // answer a question nobody asked of that point.
  if (point === '05BD') return VOWELS.map((v) => v.cp)
  return beside
}

/**
 * Points this one is never written without.
 *
 * A different relation from sharing a corner, and the difference is whether the
 * point occurs ALONE. A cholam and a shin dot each stand on their own and only
 * sometimes meet, so both forms are worth showing. The mark saying a sheva is
 * vocal is written over the letter whose sheva is under it and nowhere else —
 * so the form without the sheva is not a second case to check, it is a form
 * that does not occur, and showing it doubles every cell to say nothing.
 */
export function writtenWith(cp: string): string[] {
  return cp.toUpperCase() === '0311' ? ['05B0'] : []
}

/**
 * A letter with its points, in the order the marks are stored.
 *
 * Canonical order sorts combining marks by class, so a cholam (19) is written
 * before a sin dot (25) whatever order somebody typed them in. Getting this
 * wrong renders a different picture from the one a normalized document would
 * produce, which is the opposite of what a review is for.
 */
export function spell(letter: string, ...points: string[]): string {
  const marks = points
    .map((cp) => String.fromCodePoint(Number.parseInt(cp, 16)))
    .sort((a, b) => a.localeCompare(b))
  return (letter + marks.join('')).normalize('NFC')
}

/**
 * The letter to DEMONSTRATE a point on.
 *
 * A point alone is a speck nobody recognizes, so anything offering one draws it
 * on a letter — and the letter has to be one that takes it. Alef carries every
 * point but three: the shin and sin dots, which say which letter a shin is, and
 * the dagesh, which does not enter a guttural. Those fall back to a letter that
 * does take them.
 *
 * One letter throughout is what makes the row readable as a row. The point is
 * what differs between one chip and the next, and a letter that changes with it
 * is a second thing moving in a set being read for the first.
 */
export function showOn(cp: string): string {
  const on = lettersFor(cp)
  return on.includes('א') ? 'א' : on[0]
}

const NAMES = new Map(
  [...POINTS, ...TAAMIM].map((p) => [p.cp.toUpperCase(), p.name]),
)

/**
 * What to call a point.
 *
 * The points and the te'amim both, because a review that shows a mark it cannot
 * name is a review of the ones somebody already recognizes — and the te'amim
 * are the set where naming decides the reading: pashta and kadma are one
 * drawing told apart by where it sits, and so are the two telishas.
 *
 * A codepoint neither table names is shown as its own character, which is
 * better than hiding it.
 */
export function pointName(cp: string): string {
  return (
    NAMES.get(cp.toUpperCase()) ?? String.fromCodePoint(Number.parseInt(cp, 16))
  )
}

/**
 * Whether a line is written wholly in Hebrew letters, and so is set in the
 * Hebrew face whatever the text around it leads with. A line holding any Latin
 * letter is mixed, and stays in the face of its surroundings.
 */
export const isHebrewLine = (text: string | undefined): boolean =>
  !!text && /\p{Script=Hebrew}/u.test(text) && !/\p{Script=Latin}/u.test(text)
